"use client";
import React, { useEffect, useState } from "react";
import { X, ArrowRight } from "lucide-react";
import Select from "react-select";
import { stateCityData } from "@/utils/stateCityData";

const UserFormModal = ({ isOpen, onClose, onSuccess, shouldDownload }) => {
  const [loading, setLoading] = useState(false);
  const INITIAL_FORM_STATE = {
    name: "",
    email: "",
    phone: "",
    programme: "",
    Specialization: "",
    state: "",
    city: "",
    downloadBrochureStatus: false,
  };

  // OTP
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [otpValue, setOtpValue] = useState("");
  const [otpLoading, setOtpLoading] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const [form, setForm] = useState(INITIAL_FORM_STATE);
  const [cities, setCities] = useState([]);
  const [programmeOptions, setProgrammeOptions] = useState([]);
  const [formError, setFormError] = useState("");
  const [formSuccess, setFormSuccess] = useState("");

  const OTP_REGEX = /^\d{4}$/;
  const emailRegex = /^[a-zA-Z0-9._%+-]{4,}@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,7}$/;
  const phoneRegex = /^(?:\+91|91)?[6-9]\d{9}$/;

  const formattedStates = Object.keys(stateCityData).map((s) => ({
    value: s,
    label: s,
  }));

  const fetchCities = (stateName) => {
    const cityList = stateCityData[stateName] || [];
    setCities(cityList.map((c) => ({ value: c, label: c })));
  };

  // Lock body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
      setFormError("");
      setFormSuccess("");
    }
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isOpen]);

  // ── Reset OTP state on modal close ──
  useEffect(() => {
    if (!isOpen) {
      setOtpSent(false);
      setOtpVerified(false);
      setOtpValue("");
      setFormError("");
      setResendTimer(0);
    }
  }, [isOpen]);

  // ── Send OTP ──
  const handleSendOtp = async () => {
    if (!phoneRegex.test(form.phone)) {
      setFormError("Please enter a valid 10-digit Indian mobile number");
      return;
    }
    setOtpLoading(true);
    setFormError("");
    try {
      const res = await fetch("https://lp.msu.edu.in/lead/api/send-otp", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phoneNumber: `91${form.phone}` }),
      });
      const data = await res.json();
      if (!data.success) {
        setFormError(data.message);
        return;
      }
      setOtpSent(true);
      setResendTimer(30);
    } catch (err) {
      setFormError(err.message || "Failed to send OTP. Please try again.");
    } finally {
      setOtpLoading(false);
    }
  };

  // ── Verify OTP ──
  const handleVerifyOtp = async () => {
    if (!OTP_REGEX.test(otpValue)) {
      setFormError("OTP must be exactly 4 digits.");
      return;
    }
    setOtpLoading(true);
    setFormError("");
    try {
      const res = await fetch("https://lp.msu.edu.in/lead/api/verify-otp", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phoneNumber: `91${form.phone}`, otp: otpValue }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.message);
      setOtpVerified(true);
      setFormError("");
    } catch (err) {
      setFormError(err.message || "Invalid OTP. Please try again.");
    } finally {
      setOtpLoading(false);
    }
  };

  // Sync Download Status
  useEffect(() => {
    if (isOpen) {
      setForm((prev) => ({
        ...prev,
        downloadBrochureStatus: Boolean(shouldDownload),
      }));
    }
  }, [shouldDownload, isOpen]);

  /* ----------- FETCH COURSES ----------- */
  useEffect(() => {
    const fetchProgrammes = async () => {
      const res = await fetch(
        "https://res.cloudinary.com/dhatss6sz/raw/upload/v1767614890/updated-inquiry-pop-up_gslo8b_jnsbow.json",
      );
      const data = await res.json();
      const courses = data["Active Course"] || [];

      const grouped = {};

      courses.forEach((item) => {
        if (!item.Program || !item.Specialization) return;
        if (!grouped[item.Program]) grouped[item.Program] = new Set();
        grouped[item.Program].add(item.Specialization);
      });

      const final = Object.entries(grouped).map(([program, specs]) => ({
        program,
        specializations: Array.from(specs),
      }));

      setProgrammeOptions(final);
    };

    fetchProgrammes();
  }, []);

  /* ----------- HANDLE CHANGE ----------- */
  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setFormError("");
    setFormSuccess("");
    if (name === "state") {
      fetchCities(value);
      setForm((prev) => ({ ...prev, city: "" }));
    }
  };

  const handleSelectChange = (name, selectedOption) => {
    const value = selectedOption ? selectedOption.value : "";
    setFormError("");
    setFormSuccess("");
    if (name === "Specialization") {
      const [program, specialization] = value.split("|||");
      setForm((prev) => ({
        ...prev,
        programme: program,
        Specialization: specialization,
      }));
      return;
    }

    setForm((prev) => ({ ...prev, [name]: value }));

    if (name === "state") {
      fetchCities(value);
      setForm((prev) => ({ ...prev, city: "" }));
    }
  };

  /* ----------- SUBMIT ----------- */
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (loading) return;

    setFormError("");
    setFormSuccess("");
    if (!otpVerified) {
      setFormError(
        "Please verify your phone number with OTP before submitting.",
      );
      return;
    }
    const { name, email, phone, programme, Specialization, state, city } = form;

    if (!name.trim()) {
      setFormError("Please enter your name.");
      return;
    }

    if (!email.trim()) {
      setFormError("Please enter your email address.");
      return;
    }

    if (!emailRegex.test(email)) {
      setFormError("Please enter a valid email address.");
      return;
    }

    if (!phone.trim()) {
      setFormError("Please enter your mobile number.");
      return;
    }

    if (!phoneRegex.test(phone)) {
      setFormError("Please enter a valid 10-digit mobile number.");
      return;
    }

    if (!otpVerified) {
      setFormError("Please verify your mobile number via OTP.");
      return;
    }

    if (!programme) {
      setFormError("Please select a programme.");
      return;
    }

    if (!Specialization) {
      setFormError("Please select a specialization.");
      return;
    }

    if (!state) {
      setFormError("Please select your state.");
      return;
    }

    if (!city) {
      setFormError("Please select your city.");
      return;
    }

    setLoading(true);

    const formData = new FormData();
    Object.entries(form).forEach(([k, v]) => formData.append(k, v));

    try {
      const res = await fetch(
        "https://partner.medhaviskillsuniversity.edu.in/partnar/all-student-query-of-msu/",
        { method: "POST", body: formData },
      );

      if (!res.ok) throw new Error();

      setFormSuccess("Form submitted successfully!");
      setForm(INITIAL_FORM_STATE);
      setOtpSent(false);
      setOtpVerified(false);
      setOtpValue("");
      setResendTimer(0);
      setFormError("");
      setTimeout(() => {
        onClose();
        onSuccess?.();
      }, 3000);
    } catch (error) {
      setFormError("Submission failed");
    } finally {
      setLoading(false);
    }
  };

  /* ----------- REACT SELECT STYLES ----------- */
  const customStyles = {
    control: (base) => ({
      ...base,
      padding: "2px",
      borderColor: "#e5e7eb",
      borderRadius: "0.25rem",
      boxShadow: "none",
      "&:hover": {
        borderColor: "#d1d5db",
      },
    }),
    placeholder: (base) => ({
      ...base,
      color: "#9ca3af",
    }),
    groupHeading: (base) => ({
      ...base,
      color: "#1f2937",
      fontWeight: "bold",
    }),
  };

  /* ----------- TRANSFORM OPTIONS ----------- */
  const formattedProgrammeOptions = programmeOptions.map((group) => ({
    label: group.program,
    options: group.specializations.map((spec) => ({
      value: `${group.program}|||${spec}`,
      label: spec,
    })),
  }));

  const formattedCities = cities;

  /* ----------- UI ----------- */
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-9999 flex items-center justify-center px-3">
      <div onClick={onClose} className="absolute inset-0 bg-black/60" />

      <div className="relative w-full max-w-4xl bg-white rounded-lg overflow-hidden z-50 flex flex-col md:flex-row">
        {/* Left Side - Marketing Banner Image */}
        <div className="w-full md:w-1/2 bg-gray-200 flex items-center justify-center overflow-hidden hidden md:flex">
          <img
            src="https://cdn.msu.edu.in/msu-reavamp/banner/popup-image.webp"
            alt="Banner Popup"
            className="w-full h-full object-cover bg-[#0e74aa]"
          />
        </div>

        {/* Right Side - Form */}
        <div className="relative w-full md:w-1/2 p-6 md:p-8">
          <X
            className="cursor-pointer absolute right-4 top-4"
            onClick={onClose}
          />

          <div className="text-xl font-semibold mb-5">Let's Get You Started!</div>

        <form className="space-y-4" onSubmit={handleSubmit}>
          <input
            name="name"
            value={form.name}
            onChange={handleChange}
            placeholder="Name"
            className="w-full border border-gray-300 p-2 rounded"
          />
          <input
            name="email"
            value={form.email}
            onChange={handleChange}
            placeholder="Email"
            className="w-full border border-gray-300 p-2 rounded"
          />
          {/* <input
            name="phone"
            value={form.phone}
            onChange={handleChange}
            placeholder="Phone"
            className="w-full border border-gray-300 p-2 rounded"
          /> */}

          {/* Phone + Send OTP */}
          <div className="flex gap-2">
            <input
              name="phone"
              value={form.phone}
              onChange={(e) => {
                handleChange(e);
                if (otpSent) {
                  setOtpSent(false);
                  setOtpVerified(false);
                  setOtpValue("");
                  setFormError("");
                }
              }}
              placeholder="Phone"
              className="flex-1 border border-gray-300 p-2 rounded"
              disabled={otpVerified}
            />
            <button
              type="button"
              onClick={handleSendOtp}
              disabled={otpLoading || otpVerified || resendTimer > 0}
              className={`px-3 py-2 rounded text-sm font-semibold whitespace-nowrap transition-colors
                ${
                  otpVerified
                    ? "bg-green-100 text-green-700 border border-green-300 cursor-default"
                    : resendTimer > 0
                      ? "bg-gray-100 text-gray-400 border border-gray-200 cursor-not-allowed"
                      : "bg-[#F7A400] text-white cursor-pointer hover:bg-[#e09300]"
                }`}
            >
              {otpVerified
                ? "✓ Verified"
                : otpLoading
                  ? "Sending..."
                  : otpSent
                    ? resendTimer > 0
                      ? `Resend (${resendTimer}s)`
                      : "Resend OTP"
                    : "Send OTP"}
            </button>
          </div>

          {/* OTP Input + Verify */}
          {otpSent && !otpVerified && (
            <div className="space-y-1">
              <div className="flex gap-2">
                <input
                  value={otpValue}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, "").slice(0, 4);
                    setOtpValue(val);
                    setFormError("");
                  }}
                  placeholder="Enter 4-digit OTP"
                  maxLength={4}
                  inputMode="numeric"
                  className="flex-1 border border-gray-300 p-2 rounded tracking-widest text-center font-mono text-lg"
                />
                <button
                  type="button"
                  onClick={handleVerifyOtp}
                  disabled={otpLoading || otpValue.length !== 4}
                  className={`px-4 py-1 rounded text-sm font-semibold transition-colors
                    ${
                      otpValue.length === 4 && !otpLoading
                        ? "bg-[#F7A400] text-white cursor-pointer hover:bg-[#e09300]"
                        : "bg-gray-100 text-gray-400 border border-gray-200 cursor-not-allowed"
                    }`}
                >
                  {otpLoading ? "Verifying..." : "Verify OTP"}
                </button>
              </div>
            </div>
          )}

          <Select
            name="Specialization"
            aria-label="Select Specialization"
            className="w-full border border-gray-300 rounded"
            placeholder="Select Specialization"
            options={formattedProgrammeOptions}
            onChange={(opt) => handleSelectChange("Specialization", opt)}
            styles={customStyles}
            maxMenuHeight={200}
            value={
              form.Specialization
                ? {
                    value: `${form.programme}|||${form.Specialization}`,
                    label: form.Specialization,
                  }
                : null
            }
          />

          <Select
            name="state"
            aria-label="Select State"
            placeholder="Select State"
            className="w-full border border-gray-300 rounded"
            options={formattedStates}
            onChange={(opt) => handleSelectChange("state", opt)}
            styles={customStyles}
            maxMenuHeight={200}
            value={
              form.state
                ? formattedStates.find((s) => s.value === form.state)
                : null
            }
          />

          <Select
            name="city"
            aria-label="Select City" 
            placeholder="Select City"
            className="w-full border border-gray-300 rounded"
            options={formattedCities}
            onChange={(opt) => handleSelectChange("city", opt)}
            styles={customStyles}
            maxMenuHeight={200}
            value={
              form.city
                ? formattedCities.find((c) => c.value === form.city)
                : null
            }
          />

          {formError && (
            <p className="text-red-500 text-sm text-center">{formError}</p>
          )}

          {formSuccess && (
            <p className="text-[#0B202C] text-sm text-center">{formSuccess}</p>
          )}

          <button
            disabled={loading}
            className="w-full bg-[#F7A400] py-2 rounded font-semibold flex justify-center gap-2 cursor-pointer"
          >
            {loading ? "Submitting..." : "SUBMIT"} <ArrowRight />
          </button>
        </form>
        </div>
      </div>
    </div>
  );
};

export default UserFormModal;
